import { useEffect, useMemo, useRef, useState } from 'react';
import type { Trip } from '@/services/trips';
import type { Booking } from '@/services/bookings';
import {
  computeTripCostConfidence,
  type TripCostConfidence,
  type TripCostStyle,
} from '@/services/tripCostConfidence';
import { requestTripCostEstimate } from '@/services/tripCostEstimateAI';

type UseTripCostConfidenceArgs = {
  trip: Trip;
  bookings?: Booking[];
  style?: TripCostStyle;
  partySize?: number;
  bufferPct?: number;
  isPremium?: boolean;
  enabled?: boolean;
};

const normalizeAiError = (error: unknown): string => {
  const raw = error instanceof Error ? error.message : 'AI estimate unavailable';
  const normalized = raw.toLowerCase();

  if (normalized.includes('not-found') || normalized.includes('not found')) {
    return 'AI estimate unavailable in this environment — showing rule-based estimate';
  }

  if (
    normalized.includes('resource-exhausted') ||
    normalized.includes('too many cost estimate')
  ) {
    return 'AI estimate temporarily unavailable — showing rule-based estimate. Try again later.';
  }

  return raw;
};

export function useTripCostConfidence({
  trip,
  bookings = [],
  style = 'comfort',
  partySize = 1,
  bufferPct = 12,
  isPremium = false,
  enabled = true,
}: UseTripCostConfidenceArgs) {
  const deterministic = useMemo(
    () =>
      computeTripCostConfidence({
        trip,
        bookings,
        style,
        partySize,
        bufferPct,
      }),
    [trip, bookings, style, partySize, bufferPct],
  );

  const [aiEstimate, setAiEstimate] = useState<TripCostConfidence | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const fetchedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !isPremium) {
      setAiEstimate(null);
      setAiLoading(false);
      setAiError(null);
      fetchedKeyRef.current = null;
      return;
    }

    const cacheKey = `${trip.id}:${style}:${partySize}:${bufferPct}`;
    if (fetchedKeyRef.current === cacheKey) {
      return;
    }

    let cancelled = false;
    setAiLoading(true);
    setAiError(null);

    void requestTripCostEstimate({
      tripId: trip.id,
      style,
      partySize,
      bufferPct,
    })
      .then((result) => {
        if (cancelled) return;
        fetchedKeyRef.current = cacheKey;
        setAiEstimate(result);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setAiError(normalizeAiError(error));
        setAiEstimate(null);
      })
      .finally(() => {
        if (!cancelled) setAiLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, isPremium, trip.id, style, partySize, bufferPct]);

  const confidence: TripCostConfidence = aiEstimate ?? deterministic;

  return {
    confidence,
    deterministic,
    aiEstimate,
    aiLoading,
    aiError,
    isAiPowered: Boolean(aiEstimate),
  };
}
